/**
 * addon-cart-cleanup.js  –  loaded on EVERY page via layout/theme.liquid
 *
 * After any cart change, removes add-on line items whose main product is no
 * longer in the cart.  Add-ons carry a hidden property:
 *   _main_variant_id: "<variant id of the main product>"
 */
(() => {
  // ─── Helpers ───────────────────────────────────────────────────────────────

  function getCartEl() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  /**
   * Update the cart drawer sections in the DOM without triggering open().
   * Used after orphan removal so the drawer doesn't pop open unexpectedly.
   */
  async function refreshSectionsOnly(cartEl) {
    try {
      const ids      = cartEl.getSectionsToRender().map((s) => s.id).join(',');
      const sections = await fetch(`/?sections=${ids}`).then((r) => r.json());

      cartEl.getSectionsToRender().forEach((section) => {
        const el = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (!el || !sections[section.id]) return;
        try {
          el.innerHTML = cartEl.getSectionInnerHTML(sections[section.id], section.selector);
        } catch (_) {}
      });
    } catch (e) {
      console.warn('[addon-cleanup] section refresh failed:', e);
    }
  }

  // ─── Orphan cleanup ────────────────────────────────────────────────────────

  let busy = false;

  async function removeOrphanedAddons(eventData) {
    if (busy) return;

    // Skip when our own add-to-cart code triggered the event —
    // we just added items so there can't be orphans, and skipping
    // avoids an unnecessary /cart.js round-trip that slows things down.
    if (eventData && eventData.source === 'addon-products') return;

    const cart = await fetch('/cart.js').then((r) => r.json()).catch(() => null);
    if (!cart || !cart.items.length) return;

    // Variant IDs of items that are NOT add-ons (no _main_variant_id property)
    const mainVariantIds = new Set(
      cart.items
        .filter((item) => !item.properties || !item.properties['_main_variant_id'])
        .map((item) => String(item.variant_id))
    );

    // Add-ons whose linked main product is gone
    const orphans = cart.items.filter((item) => {
      const linkedId = item.properties && item.properties['_main_variant_id'];
      return linkedId && !mainVariantIds.has(String(linkedId));
    });

    if (!orphans.length) return;

    busy = true;
    try {
      const updates = {};
      orphans.forEach((item) => { updates[item.key] = 0; });

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ updates }),
      }).then((r) => r.json());

      // ── Refresh the UI ────────────────────────────────────────────────────

      const onCartPage = window.location.pathname.startsWith('/cart');

      if (onCartPage) {
        // Reload so the cart page re-renders without the orphaned add-ons
        window.location.reload();
      } else {
        // Update cart drawer + icon count in place (without opening the drawer)
        const cartEl = getCartEl();
        if (cartEl) await refreshSectionsOnly(cartEl);
      }
    } catch (e) {
      console.error('[addon-cleanup] error removing orphaned add-ons:', e);
    } finally {
      busy = false;
    }
  }

  // ─── Hook into Dawn's pub/sub ──────────────────────────────────────────────

  function setup() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
      setTimeout(setup, 50);
      return;
    }
    subscribe(PUB_SUB_EVENTS.cartUpdate, removeOrphanedAddons);
  }

  setup();
})();
